import {
  cityRefSchema,
  languageSchema,
  lastKnownLocationSourceSchema,
  outreachChannelSchema,
  User,
  userMetaPermissionStateSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../types';

/**
 * `PATCH /current-user/meta` — writer for the agent-facing signal
 * layer described in `docs/ai/user-signals.md`.
 *
 * The endpoint is a granular, orthogonal PATCH: mobile clients and
 * app-level code send only the sections that actually changed. The
 * backend `UserMetaService` merges each section defensively — never
 * overwriting a higher-fidelity signal (e.g. `location.accurate`
 * from GPS) with a lower-fidelity one (e.g. IP or user-stated).
 *
 * Every field is optional. An empty body is a valid (no-op) request.
 *
 * The "which app / which platform is calling?" identity is NOT part
 * of the body — it rides on the paired `x-hero-app` + `x-hero-platform`
 * headers that the SDK attaches to every request. The endpoint
 * derives `(app, platform)` from those headers and writes into the
 * matching `User.meta.presence.apps[i]` entry (upsert by
 * `(app, platform)`). See `libs/shared/sdks/core-sdk/src/config/sdk-config.ts`.
 */
export const currentUserMetaUpdateAPISchema = z.object({
  /** IANA timezone the user is in (e.g. `Asia/Riyadh`). Cross-app. */
  timezone: z.string().trim().optional(),
  /**
   * Free-form app version the client is on (e.g. "1.42.0"). Stored
   * on `User.meta.presence.apps[i].appVersion` for the matching
   * `(app, platform)` entry. Sent on login / app-open; there's no
   * point resending it on every foreground.
   */
  appVersion: z.string().trim().optional(),
  /**
   * Free-form OS version the client is on (e.g. "17.4.1" on iOS,
   * "14" on Android). Stored on `User.meta.presence.apps[i].osVersion`.
   */
  osVersion: z.string().trim().optional(),
  /**
   * Snapshot of mobile OS permissions the user has granted /
   * denied. Clients send whatever the OS is currently reporting;
   * the backend timestamps each one on receive and stores it on
   * `User.meta.presence.apps[i].permissions` — permissions are
   * per-(app, platform), NOT global on the user.
   */
  permissions: z
    .object({
      fineLocation: userMetaPermissionStateSchema.optional(),
      backgroundLocation: userMetaPermissionStateSchema.optional(),
      notifications: userMetaPermissionStateSchema.optional(),
    })
    .optional(),
  /**
   * User-stated approximate location — e.g. the user typed "I'm in
   * Riyadh" in chat. Writes `meta.location.approximate` with
   * `source: 'user_stated'`. Never touches `meta.location.accurate`.
   */
  statedLocation: z
    .object({
      city: cityRefSchema,
      /** Free-form verbatim quote from the user. Best-effort. */
      note: z.string().trim().optional(),
    })
    .optional(),
  /**
   * Explicit channel preferences. Merges over any existing values;
   * pass an empty array to clear a specific field.
   */
  channels: z
    .object({
      policy: z
        .object({
          preferred: z.array(outreachChannelSchema).optional(),
          blocked: z.array(outreachChannelSchema).optional(),
        })
        .optional(),
      /** Hard opt-out from ALL outreach. */
      outreachOptOut: z.boolean().optional(),
      /** DND window in the user's local time. */
      dnd: z
        .object({
          start: z.string().regex(/^\d{2}:\d{2}$/),
          end: z.string().regex(/^\d{2}:\d{2}$/),
        })
        .nullable()
        .optional(),
      /** Language for proactive outreach. */
      outreachLanguage: languageSchema.optional(),
    })
    .optional(),
});
export type CurrentUserMetaUpdateAPI = API<
  z.infer<typeof currentUserMetaUpdateAPISchema>,
  User
>;

/**
 * `GET /current-user/meta` — read the caller's `User.meta` block.
 * Returns the freshly-loaded `User` with `meta` populated. Included
 * so mobile clients can hydrate their local presence / permissions
 * cache without pulling the entire `/auth/me` payload.
 *
 * Kept close to the write endpoint so both live and evolve together.
 */
export type CurrentUserMetaGetAPI = API<undefined, User>;

/**
 * Re-export the location source enum so agent tools (e.g.
 * `set_user_stated_location`) that share the write payload shape
 * can reference the same union without re-declaring it.
 */
export { lastKnownLocationSourceSchema };
